import React, { useContext, useState, useEffect } from 'react';

import { makeStyles } from '@mui/styles';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import useMetric from '../../../lib/hooks/useMetric';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { PatientCheckInHistoryRow } from './index';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    marginTop: 16,
  },
  topBar: {
    marginBottom: 16,
  },
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
}));

const PatientCheckInHistoryBox = ({
  summary,
  setSetSubtab,
  checkinHistory: providedCheckinHistory,
  isLoading: providedIsLoading,
}) => {
  const classes = useStyles();
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);

  // Only fetch data if it wasn't provided as a prop
  const { isLoading: hookIsLoading, data: hookData } = useMetric(
    'checkin_history',
    providedCheckinHistory ? null : currentTab.id,
  );

  // Use provided data or fall back to data from the hook
  const isLoading = providedIsLoading !== undefined ? providedIsLoading : hookIsLoading;
  const data = providedCheckinHistory ? { metric: { value: providedCheckinHistory } } : hookData;

  // Add state for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLastPage, setIsLastPage] = useState(false);

  // Update isLastPage when data changes
  useEffect(() => {
    if (data && data.metric && data.metric.value) {
      setIsLastPage((page + 1) * rowsPerPage >= data.metric.value.length);
    }
  }, [data, page, rowsPerPage]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    if (!isLastPage || newPage < page) {
      setPage(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className="patient-checkin-history-box"
          id="patient-checkin-history-box"
        >
          <Grid
            item
            container
            xs={12}
            className={classes.topBar}
            justifyContent="space-between"
            sx={{ width: '100%', maxWidth: '100%', mx: 0, padding: 0 }}
          >
            <Grid item>
              <Typography variant={'h2'}>Check-In History</Typography>
            </Grid>
          </Grid>

          {isLoading ? (
            <AdminProgressBar />
          ) : (
            <Box className={classes.container}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="left">Date</TableCell>
                    <TableCell align="left">Challenge</TableCell>
                    <TableCell align="left">Day #</TableCell>
                    <TableCell align="left">Feeling</TableCell>
                    <TableCell align="left">Meal Type</TableCell>
                    <TableCell align="left">Checked-In</TableCell>
                    <TableCell align="left">Paused</TableCell>
                    <TableCell align="left">Succeeded</TableCell>
                    <TableCell align="left">User Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data &&
                    data.metric &&
                    data.metric.value
                      .slice(summary ? 0 : page * rowsPerPage, summary ? 3 : page * rowsPerPage + rowsPerPage)
                      .map((item, index) => {
                        return (
                          <PatientCheckInHistoryRow
                            key={index}
                            row={item}
                            summary={summary}
                          />
                        );
                      })}
                </TableBody>
              </Table>
            </Box>
          )}

          {!summary && !isLoading && data && data.metric && data.metric.value && data.metric.value.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={data.metric.value.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              getItemAriaLabel={(type) => `Go to ${type} page`}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientCheckInHistoryBox;
